import { BarChartOutlined, DownloadOutlined, InfoCircleOutlined, LineChartOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Radio, Select, Tooltip, Typography } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components/user-name.component'
import { Customer } from '@/modules/customer/customer.schema'
import { departmentsMapAtom } from '@/modules/department'
import { Devlop } from '@/modules/devlop/devlop.schema'
import { Production } from '@/modules/production-project/production.schema'
import { usersAtom } from '@/modules/user'
import { ResourceType } from '@/schemas'

import { ProjectDetailTable } from '../detail-table/project-detail-table'
import { UserDetailTable } from '../detail-table/user-detail-table'
import { DashboardLineChart } from '../user-resource-dashboard-chart/dashboard-chart-line'

export const BigProjectTrendDashboardCard: React.FC<{
  type: 'project' | 'user'
  selfId: string
  resourceType: ResourceType
  resource: Devlop | Customer | Production
}> = ({ type, selfId, resourceType, resource }) => {
  const { t } = useTranslation()

  const [users] = useAtom(usersAtom)
  const [departmentsMap] = useAtom(departmentsMapAtom)

  const [selected, setSelected] = React.useState()
  const [chartType, setChartType] = React.useState<'line' | 'interval'>('line')
  const [selectOptions, setSelectOptions] = React.useState<any[]>([])

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data: trendProjectsData, run: runTrendProjectsData } = useRequest(
    (selfIds?: string[]) => ({
      url: '/api/work-resource/statistics/specific-project/trend/projects/data',
      params: {
        startDate,
        endDate,
        resourceType,
        selfId,
        selfIds,
      },
    }),
    {
      manual: true,
      initialData: { chart: [], list: [] },
    }
  )

  const { data: trendProjectsList, run: runTrendProjectsList } = useRequest(
    {
      url: '/api/work-resource/statistics/specific-project/trend/projects/list',
      params: {
        startDate,
        endDate,
        selfId,
        resourceType,
      },
    },
    {
      manual: true,
      initialData: [],
    }
  )

  const { data: trendPeopleData, run: runTrendPeopleData } = useRequest(
    (email?: string) => ({
      url: '/api/work-resource/statistics/specific-project/trend/people/data',
      params: {
        startDate,
        endDate,
        resourceType,
        selfId,
        emails: [email],
      },
    }),
    {
      manual: true,
      initialData: { chart: [], list: [] },
    }
  )

  const { data: trendPeopleList, run: runTrendPeopleList } = useRequest(
    {
      url: '/api/work-resource/statistics/specific-project/trend/people/list',
      params: {
        startDate,
        endDate,
        selfId,
        resourceType,
      },
    },
    {
      manual: true,
      initialData: [],
    }
  )

  const { run: runDownloadExcel } = useRequest(
    {
      url: '/api/work-resource/statistics/specific-project/trend/people/excel',
      method: 'POST',
      data: {
        startDate,
        endDate,
        resourceType,
        selfId,
      },
      responseType: 'blob',
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        saveAs(res, `${selfId}-${resource.name}-人员资源明细-${startDate}-${endDate}.xlsx`)
      },
    }
  )

  const list = React.useMemo(() => {
    if (type === 'project') {
      return (trendProjectsList as any) || []
    }
    return (trendPeopleList as any) || []
  }, [trendProjectsList, trendPeopleList])

  const data = React.useMemo(() => {
    if (type === 'project') {
      return (trendProjectsData as any) || { chart: [], list: [] }
    }
    return (trendPeopleData as any) || { chart: [], list: [] }
  }, [trendProjectsData, trendPeopleData, type])

  React.useEffect(() => {
    if (startDate && endDate && selfId) {
      if (type === 'project') {
        runTrendProjectsList()
      } else {
        runTrendPeopleList()
      }
    }
  }, [startDate, endDate, selfId, type])

  React.useEffect(() => {
    if (list.length > 0) {
      if (type === 'project') {
        setSelectOptions(
          list!.map((p: any) => (
            <Select.Option key={p.selfId} value={p.selfId} name={p.name}>
              <span>{p.name}</span>
            </Select.Option>
          ))
        )
        setSelected(list!.slice(0, 6).map((l: any) => ({ value: l.selfId, name: l.name })) as any)
      } else {
        setSelectOptions(
          list!.map((u: any) => {
            const user = users.find((user) => user.email === u.email)
            // if (!user) return
            const email = user?.email || u.email
            const name = user?.name || u.email
            return (
              <Select.Option key={email} value={email} user={user} name={email}>
                <div className='flex'>
                  <UserAvatar className='mr-8px' size='small' email={email} />
                  <div className='text-xs leading-tight'>
                    <div>{name}</div>
                    <Typography.Text type='secondary'>
                      {user?.departmentIds
                        .map((id) => departmentsMap![id]?.name)
                        .filter(Boolean)
                        .join(',')}
                    </Typography.Text>
                  </div>
                </div>
              </Select.Option>
            )
          })
        )
        list[0] ? setSelected({ value: list[0].email, name: list[0].email } as any) : setSelected(undefined)
      }
    }
  }, [list, type])

  React.useEffect(() => {
    if (startDate && endDate && selfId) {
      if (type === 'project') {
        // fetch new Data selected => []
        selected && runTrendProjectsData((selected as any[]).map((s) => s.value))
      } else if (type === 'user') {
        // fetch new Data selected => {}
        selected ? runTrendPeopleData(selected['value']) : runTrendPeopleData(undefined)
      }
    }
  }, [selected, type])

  return (
    <div className='flex mb-8'>
      <div className='flex-grow mr-16'>
        <div className='flex mb-4 justify-between'>
          <div className='flex font-medium text-lg gap-1 items-baseline'>
            {t(`${type === 'project' ? '项目' : '人员'}视角`)}
            <Tooltip
              title={
                type === 'project'
                  ? '默认展示时间范围内该项目资源消耗做多的6个普通/自定义项目'
                  : '默认展示时间范围内该项目工时最多的人，显示该人员所在的所有普通/自定义项目，项目分摊比例已纳入计算。'
              }
            >
              <InfoCircleOutlined className='cursor-pointer text-sm' size={14} />
            </Tooltip>
          </div>
          <div className='flex'>
            <Select
              className='mr-4 w-60'
              labelInValue
              mode={type === 'project' ? 'multiple' : undefined}
              maxTagCount={1}
              value={selected}
              onChange={(v) => setSelected(v)}
              showSearch
              showArrow
              optionFilterProp='name'
            >
              {selectOptions}
            </Select>
            <Radio.Group onChange={(e) => setChartType(e.target.value)} value={chartType}>
              <Radio.Button value='line'>
                <LineChartOutlined />
              </Radio.Button>
              <Radio.Button value='interval'>
                <BarChartOutlined />
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <DashboardLineChart
          data={data!.chart.map((c: any) => ({
            ...c,
            date: t('dashboard.xAxisLabel', { year: c.year, week: c.week }),
          }))}
          type={chartType}
        />
      </div>
      <div className='flex-shrink-0 w-90'>
        <div className='flex font-medium text-lg mb-4 items-baseline justify-between'>
          <span className='flex items-baseline gap-1'>
            {t(`${type === 'project' ? '项目' : '人员'}资源明细`)}
            <Tooltip
              title={
                type === 'project'
                  ? '项目工时百分比：该项目总工时/所有项目工时；团队工时百分比：该项目团队工时/该团队总工时，项目分摊比例已纳入计算。'
                  : '该项目的人员工时/人员总工时，项目分摊比例已纳入计算'
              }
            >
              <InfoCircleOutlined className='cursor-pointer text-sm' size={14} />
            </Tooltip>
          </span>
          {type === 'user' && (
            <Tooltip title={t('下载')}>
              <Button shape='circle' icon={<DownloadOutlined />} onClick={() => runDownloadExcel()} />
            </Tooltip>
          )}
        </div>
        {type === 'project' ? (
          <ProjectDetailTable list={data!.list as any} type='big-project' />
        ) : (
          <UserDetailTable list={data!.list as any} />
        )}
      </div>
    </div>
  )
}
